/**
 * Composable for handling original products (seats, equipment) during rebooking
 */
import { computed } from 'vue'
import { useBookingStore } from '../../stores/bookingStore'
import { useMyTravelStore } from '../../stores/myTravelStore'
import { normalizePrice } from '../../utils/priceNormalization'
import type { BookingData, BookingSegment, BookingProduct } from '../../types/bookingTypes'
import type { NormalizedPrice } from '../../utils/priceNormalization'
import type { SelectedSeat } from '../../types/seatTypes'

export function useRebookingProducts() {
  const bookingStore = useBookingStore()
  const myTravelStore = useMyTravelStore()
  
  const bookingData = computed(() => bookingStore.bookingData)
  
  /**
   * Extract original seat products from booking data for a specific direction
   */
  const extractOriginalSeats = (
    bookingData: BookingData | null, 
    direction: 'outbound' | 'inbound'
  ): SelectedSeat[] => {
    if (!bookingData) return []

    const segments = direction === 'outbound' 
      ? bookingData.outbound_booking_tariff_segments 
      : bookingData.inbound_booking_tariff_segments
    
    if (!segments?.length) return []
    
    const activeSegment = segments.find((segment: BookingSegment) => {
      const hasActiveProducts = segment.additional_products?.some(
        (product: BookingProduct) => !product.cancelled && product.type === 'SF'
      )
      return hasActiveProducts
    })
    
    if (!activeSegment) return []
    
    const selectedSeats: SelectedSeat[] = []
    
    activeSegment.additional_products?.forEach((product: BookingProduct) => {
      if (product.type === 'SF' && !product.cancelled) {
        const normalizedPrice: NormalizedPrice = normalizePrice(product.price)
        const priceWithOreFlag   = {
          ...normalizedPrice,
          inOre: true
        }
        selectedSeats.push({
          id: product.item_id?.toString() || '',
          property_code: product.code?.toString() || '',
          price: priceWithOreFlag,
          status: 'confirmed', 
          seat_number: product.name?.toString() || '',
          sequence_number: 0, 
          x_pos: 0, 
          y_pos: 0, 
          
          passenger_id: product.passenger_id?.toString() || '',
          seat_id: product.item_id?.toString() || '',
          strategy: 'manual', 
          seat_preferences: [],
          itemId: product.item_id?.toString() || '',
          direction: direction,
          legIndex: 0, 
          segmentId: activeSegment.id?.toString() || '',
          serviceIdentifier: '',
          departureStation: activeSegment.departure_station?.name || '',
          arrivalStation: activeSegment.arrival_station?.name || '',
          tariffCode: product.tariff_code?.toString() || '',
          carriage: product.seat?.carriage || product.carriage || product.item_data?.carriage || '1', 
          passengerId: product.passenger_id?.toString() || '',
          
          item_ref: product.item_ref?.toString()
        })
      }
    })
    
    return selectedSeats
  }
  
  /**
   * Load original seat products into myTravelStore for a specific direction
   */
  const loadOriginalSeats = (direction: 'outbound' | 'inbound') => {
    if (!myTravelStore.isRebooking) return
    
    const seats = extractOriginalSeats(bookingData.value, direction)
    
    if (seats.length) {
      
      const totalPrice = seats.reduce((sum, seat) => {
        const priceObj = seat.price as any
        let seatPrice = priceObj?.amount || 0
        
        if (priceObj?.inOre === true) {
          seatPrice = seatPrice / 100
        }
        
        return sum + seatPrice
      }, 0)
      
      myTravelStore.trackOriginalProductPrices(direction, 'seats', totalPrice)      
    } 
  }
  
  /**
   * Extract original equipment products from booking data for a specific direction
   */
  const extractOriginalEquipment = (
    bookingData: BookingData | null, 
    direction: 'outbound' | 'inbound'
  ) => {
    if (!bookingData) return []

    const segments = direction === 'outbound' 
      ? bookingData.outbound_booking_tariff_segments 
      : bookingData.inbound_booking_tariff_segments
    
    if (!segments?.length) {
      return []
    }
    
    const supportedProductCodes = [
      'DG', 'BK', 'FB', 'LU', 'PR', 'VC', 'IN', 'ET', 
      'DS', 'PE', 'HO', 'WI', 'MD', 'ME', 'TA', 'PA', 'LT', 'AU'
    ]
    
    const equipmentProducts: any[] = []
    const productCounts: Record<string, number> = {}
    
    segments.forEach((segment: BookingSegment) => {
      segment.additional_products?.forEach((product: BookingProduct) => {
        const productCode = product.code?.toString() || ''
        
        const isEquipmentByCode = supportedProductCodes.includes(productCode)
        const isEquipmentByType = product.type === 'EQ' || product.type === 'equipment'
        
        if (!product.cancelled && (isEquipmentByCode || isEquipmentByType)) {          
          productCounts[productCode] = (productCounts[productCode] || 0) + 1          
          let priceValue = 0
          
          if (typeof product.price === 'number') {
            priceValue = product.price
          } else if (typeof product.price === 'string') {
            priceValue = parseFloat(product.price) || 0
          } else if (typeof product.price === 'object' && product.price !== null) {
            const priceObj = product.price as { amount?: number }
            if (priceObj.amount !== undefined) {
              priceValue = priceObj.amount
            }
          }
          
          if (priceValue === 0 && product.passenger_fares && product.passenger_fares.length > 0) {
            const fare = product.passenger_fares[0]
            if (fare.price && typeof fare.price === 'object' && fare.price.amount !== undefined) {
              priceValue = fare.price.amount
            }
          }
          
          if (priceValue === 0 && product.total_price) {
            if (typeof product.total_price === 'object' && product.total_price.amount !== undefined) {
              priceValue = product.total_price.amount
            } else if (typeof product.total_price === 'number') {
              priceValue = product.total_price
            }
          }
          
          const normalizedPrice = normalizePrice(priceValue)
          
          const existingProductIndex = equipmentProducts.findIndex(
            (item) => item.product_code === productCode
          )
          
          if (existingProductIndex >= 0) {
            equipmentProducts[existingProductIndex].count++
          } else {
            equipmentProducts.push({
              product_code: productCode,
              product_type: productCode,
              name: product.name?.toString() || '',
              count: 1,
              price: normalizedPrice,
              passenger_fares: product.passenger_id ? [{
                passenger_id: product.passenger_id.toString(),
                price: normalizedPrice
              }] : [],
              tariff_code: product.tariff_code?.toString() || '',
              item_id: product.item_id?.toString() || '',
              item_ref: product.item_ref?.toString(),
              cancelled: false
            })
          }
        }
      })
    })
    
    return equipmentProducts
  }
  
  /**
   * Load original equipment products into myTravelStore for a specific direction
   */
  const loadOriginalEquipment = (direction: 'outbound' | 'inbound') => {
    if (!myTravelStore.isRebooking) return
    
    const equipment = extractOriginalEquipment(bookingData.value, direction)
    
    if (equipment.length) {
      const totalPrice = equipment.reduce((sum, item) => {
        const priceObj = item.price as any
        let itemPrice = priceObj?.amount || 0
        let isConverted = false
        
        if (priceObj?.inOre === true) {
          itemPrice = itemPrice / 100
          isConverted = true
        } else if (itemPrice > 1000) {
          itemPrice = itemPrice / 100
          isConverted = true
        } else if (typeof item.price === 'object' && item.price !== null) {
          const currencyCode = item.price.currency_code || item.price.currency || ''
          if (currencyCode === 'ØRE' || currencyCode === 'ORE') {
            console.log(`[DEBUG] Converting equipment price from öre to kroner based on currency code: ${itemPrice} öre -> ${itemPrice / 100} NOK`)
            itemPrice = itemPrice / 100
            isConverted = true
          }
        }
        
        return sum + (itemPrice * item.count)
      }, 0)
            
      myTravelStore.trackOriginalProductPrices(direction, 'equipment', totalPrice)
      myTravelStore.updateGroupedEquipment(direction, equipment, true)
    }
  }
  
  return {
    loadOriginalSeats,
    loadOriginalEquipment,
    extractOriginalSeats,
    extractOriginalEquipment
  }
}
